// api.js
import axios from 'axios';
// Importa la store y la función de utilidad
import { useAuthStore, getAuthToken } from '../store/useAuthStore'; 

// Configuración de la API
const API_URL = false ? 'http://localhost:5000/api' : 'https://api.noemibejaranoaparicio.com/api/';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// ===========================================
// INTERCEPTOR DE REQUEST (AGREGAR TOKEN)
// ===========================================
api.interceptors.request.use(
    (config) => {
        // 🚨 Obtener el token directamente de la store de Zustand
        const token = getAuthToken(); 
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// ===========================================
// DEFINICIÓN DE SERVICIOS
// ===========================================

// --- Servicios de Contenido General ---
export const siteService = {
    getSite: () => api.get('/content/site'),
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('image', file);
        return api.post('/content/site/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    // 2. Guarda el objeto site completo (con las nuevas URLs)
    updateSite: (siteData) => api.put('/content/site', siteData)
};



// --- Servicios de Administración ---
export const userService = {
    getUsers: () => api.get('/admin/users'),
    updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
    createUser: (userData) => api.post('/admin/users', userData)
};

// services/api.js (o donde tengas definidos tus servicios)

// Importa tu instancia de Axios o fetch
// import { api } from './apiConfig';

export const teamService = {
    getTeam: () => api.get('/content/team'),
    createTeamMember: (data) => api.post('/content/team', data),
    updateTeamMember: (id, data) => api.put(`/content/team/${id}`, data),
    // 🚨 Adaptado a la ruta de inactivación del backend
    deleteTeamMember: (id) => api.post(`/content/team/${id}/inactivate`) 
};

export const eventsService = {
    getEvents: () => api.get('/content/events'),
    createEvent: (data) => api.post('/content/events', data),
    updateEvent: (id, data) => api.put(`/content/events/${id}`, data),
    // 🚨 Adaptado
    deleteEvent: (id) => api.post(`/content/events/${id}/inactivate`) 
};

export const newsService = {
    getNews: () => api.get('/content/news'),
    createNewsItem: (data) => api.post('/content/news', data),
    updateNewsItem: (id, data) => api.put(`/content/news/${id}`, data),
    // 🚨 Adaptado
    deleteNewsItem: (id) => api.post(`/content/news/${id}/inactivate`) 
};

export const mapService = {
    getDistricts: () => api.get('/content/districts'),
    createDistrict: (data) => api.post('/content/districts', data),
    updateDistrict: (id, data) => api.put(`/content/districts/${id}`, data),
    // 🚨 Adaptado
    deleteDistrict: (id) => api.post(`/content/districts/${id}/inactivate`) 
};

export const proposalService = {
    getProposals: () => api.get('/content/proposals'),
    createProposal: (data) => api.post('/content/proposals', data),
    updateProposal: (id, data) => api.put(`/content/proposals/${id}`, data),
    // 🚨 Adaptado, usa la ruta ya existente en el backend para inactivar
    deleteProposal: (id) => api.post(`/content/proposals/${id}/inactivate`), 
    inactivateProposal: (id) => api.post(`/content/proposals/${id}/inactivate`), // Se puede mantener o eliminar, ya que deleteProposal hace lo mismo
    voteProposal: (id) => api.post(`/content/proposals/${id}/vote`)
};

// 🚨 Servicios para las nuevas rutas completadas en el backend
export const suggestionService = {
    getSuggestions: () => api.get('/content/suggestions'),
    getMySuggestions: () => api.get('/content/mysuggestions'),
    createSuggestion: (data) => api.post('/content/suggestions', data),
    updateSuggestion: (id, data) => api.put(`/content/suggestions/${id}`, data),
    deleteSuggestion: (id) => api.post(`/content/suggestions/${id}/inactivate`),
};

export const challengeService = {
    getChallenges: () => api.get('/content/challenges'),
    createChallenge: (data) => api.post('/content/challenges', data),
    updateChallenge: (id, data) => api.put(`/content/challenges/${id}`, data),
    deleteChallenge: (id) => api.post(`/content/challenges/${id}/inactivate`),
    completeChallenge: (id) => api.post(`/content/challenges/${id}/complete`),
};

// ... y cualquier otro servicio (por ejemplo, AssociationService)
export const associationService = {
    getAssociations: () => api.get('/content/associations'),
    createAssociation: (data) => api.post('/content/associations', data),
    updateAssociation: (id, data) => api.put(`/content/associations/${id}`, data),
    deleteAssociation: (id) => api.post(`/content/associations/${id}/inactivate`),
};

// --- Servicio de Autenticación (authService) ---
export const authService = {
    login: async (credentials) => {
        const response = await api.post('/auth/login', credentials);
        const user = response.data;
        
        // 🚨 Guardar sesión en la store de Zustand
        useAuthStore.getState().setSession(user.token, user); 
        
        return response.data;
    },
    register: async (userData) => {
        const response = await api.post('/auth/register', userData);
        return response.data;
    },
    logout: () => {
        // 🚨 Limpiar sesión usando la acción de Zustand
        useAuthStore.getState().logout();
    },
    
    // Métodos para obtener datos reactivos de la sesión
    isAuthenticated: () => useAuthStore.getState().isAuthenticated(),
    getToken: () => useAuthStore.getState().token,
    getCurrentUser: () => useAuthStore.getState().user,

    // 🌟 NUEVO MÉTODO: Verificar si el usuario es Administrador
    isAdmin: () => {
        const user = useAuthStore.getState().user;
        // Asume que el rol se almacena en user.role y que el rol de administrador es 'admin'
        return !!user && user.role === 'admin'; 
    },
};

// ===========================================
// INTERCEPTOR DE RESPONSE (MANEJAR 401)
// ===========================================
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Si el servidor responde con 401 (No Autorizado), limpiamos la sesión global
            authService.logout(); 
            
            // NOTA: La redirección a /login debe hacerse en un componente de React 
            // que escuche los cambios en useAuthStore (si el token es null)
        }
        return Promise.reject(error);
    }
);

export default api;